import { Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Project } from 'src/app/model/project/project';
import Asset from 'src/app/model/asset';
import ProductHelper from '../../../../helpers/product-helper';
import ProductPhoneNumber from '../../../../model/product/product-phone-number';
import { SubProject } from '../../../../model/project/sub-project';
import Board from 'src/app/model/product/board/board';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Company from 'src/app/model/company/company';
import ProductPhoneNumberGroup from 'src/app/display-model/product-phone-number-group';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { DO_CHECK_DEBOUNCE_TIME } from 'src/globals/globals';
import { BoardPartFormHelper } from 'src/app/shared/project-components/board-form/board-part-form/board-part-form-helper';
import { FileHelper } from '../../../../helpers/file-helper';

@Component({
  selector: 'board-summary-card',
  templateUrl: './board-summary-card.component.html',
  styleUrls: ['./board-summary-card.component.scss'],
  providers: [FormatPhoneNumber],
})
export class BoardSummaryCardComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit
{
  @Input()
  inputBoard: Board;
  @Input()
  inputSubProject: SubProject;
  @Input()
  inputProject: Project;
  @Input()
  index = 0;
  @Input()
  productPrice: string;
  @Input()
  client = false;
  definitiveDesign: Asset[];
  isImage = FileHelper.isImage;
  typeTranslation: string;
  projectContactToCompany = new Map<number, Company>();
  productPhoneNumberGroups: ProductPhoneNumberGroup[] = [];
  multiplePhoneNumbers: boolean = false;
  atLeastOneRetailer: boolean = false;
  atLeastOneSignSize: boolean = false;
  atLeastOneNoSignSize: boolean = false;
  totalPrice: number = 0;

  constructor(
    public dialog: MatDialog,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private formatPhoneNumber: FormatPhoneNumber
  ) {
    super();
  }

  ngOnInit() {
    this.setDefinitiveDesign();
    this.typeTranslation = ProductHelper.getType(this.inputBoard.type);
    this.calculateProjectContactInfo();
    this.calculateProductPhoneNumberGroups();
    this.calculatePricePreviewDisplay();
    this.initializeSubscriptions();
  }

  initializeSubscriptions() {
    const calculateTotalObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservableService.getProductPartDeletedSubscribable(),
      this.projectDisplayObservableService.getProductPlacementVariableChangedSubscribable(),
      this.projectDisplayObservableService.getProductSizeChangedSubscribable()
    );

    this.addSubscription(
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable(),
      () => {
        this.calculateProjectContactInfo();
        this.calculateProductPhoneNumberGroups();
        this.calculatePricePreviewDisplay();
      },
      () => {},
      DO_CHECK_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProductPhoneNumberChangedSubscribable(),
      () => {
        this.calculateProjectContactInfo();
        this.calculateProductPhoneNumberGroups();
      },
      () => {},
      DO_CHECK_DEBOUNCE_TIME
    );
    this.addSubscription(calculateTotalObservable, () =>
      this.calculatePricePreviewDisplay()
    );
  }

  setDefinitiveDesign() {
    this.definitiveDesign = [];
    if (this.inputBoard.productAssets) {
      for (let pa of this.inputBoard.productAssets) {
        if (pa.formSection === 'internalDesign' && !pa.archived) {
          this.definitiveDesign.push(pa.asset);
        }
      }
    }
  }

  getCompanyName(ppn: ProductPhoneNumber) {
    if (ppn.projectContact != null) {
      for (let pc of this.inputProject.projectContacts) {
        if (ppn.projectContact.id === pc.id) {
          return pc.company.regularName;
        }
      }
    }
  }

  getTitle(title: string) {
    return title == null || title === '' ? 'Geen titel' : title;
  }

  getTypeTranslation(type) {
    return ProductHelper.getType(type);
  }

  shortenName(name: string) {
    name = name.trim().replace(' ', '');
    let result = name.trim().replace(' ', '');
    let split = name.split('.');
    if (split.length > 1) {
      if (name.length > 8) {
        result =
          split[0].substring(0, 8 - split[split.length - 1].length) +
          '...' +
          split[split.length - 1];
      }
    }
    return result;
  }

  isEmpty(productPhoneNumber: ProductPhoneNumber) {
    return (
      productPhoneNumber.phoneNumbers == null ||
      productPhoneNumber.phoneNumbers === '' ||
      productPhoneNumber.phoneNumbers === 'Geen'
    );
  }

  nonEmptyPhoneNumber(): ProductPhoneNumber[] {
    let result = [];
    for (let ppn of this.inputBoard.productPhoneNumbers) {
      if (!this.isEmpty(ppn)) {
        if (ppn.projectContact != null) {
          result.push(ppn);
        }
      }
    }
    return result;
  }

  atLeastTwoContactsWithPhoneNumber() {
    let companies = new Set<number>();
    for (let ppn of this.inputBoard.productPhoneNumbers) {
      if (!this.isEmpty(ppn)) {
        companies.add(ppn.projectContact.id);
      }
      if (companies.size >= 2) {
        return true;
      }
    }
    return false;
  }

  getPhonesforCompanyName(cn: ProductPhoneNumber): ProductPhoneNumber[] {
    let result = [];
    for (let ppn of this.nonEmptyPhoneNumber()) {
      if (this.getCompanyName(cn) === this.getCompanyName(ppn)) {
        result.push(ppn);
      }
    }
    return result;
  }

  getNonEmptyPhoneNumbersForEachCompany(): ProductPhoneNumber[][] {
    let result = [];
    let companyNames = [];
    for (let nonempty of this.nonEmptyPhoneNumber()) {
      if (!companyNames.includes(this.getCompanyName(nonempty))) {
        companyNames.push(this.getCompanyName(nonempty));
        result.push(this.getPhonesforCompanyName(nonempty));
      }
    }
    return result;
  }

  calculateProjectContactInfo() {
    this.projectContactToCompany.clear();
    let atLeastOneRetailer = false;
    if (CollectionHelper.isPopulated(this.inputProject.projectContacts)) {
      this.inputProject.projectContacts.forEach((pc) => {
        if (pc.company != null) {
          this.projectContactToCompany.set(pc.id, pc.company);
          atLeastOneRetailer = atLeastOneRetailer || pc.company.isRetailer;
        }
      });
    }
    this.atLeastOneRetailer = atLeastOneRetailer;
  }

  calculateProductPhoneNumberGroups() {
    if (!this.client || (this.client && this.inputProject.id != null)) {
      this.calculateProductPhoneNumbersWithExistingProjectContacts();
    } else {
      this.calculateProductPhoneNumbersClient();
    }
  }

  calculateProductPhoneNumbersWithExistingProjectContacts() {
    const companyToPhoneNumbers = new Map<number, ProductPhoneNumber[]>();
    if (CollectionHelper.isPopulated(this.inputBoard.productPhoneNumbers)) {
      this.inputBoard.productPhoneNumbers.forEach((n) => {
        if (!this.isEmpty(n) && n.projectContact != null) {
          const company = this.projectContactToCompany.get(n.projectContact.id);
          if (company != null) {
            let arr = companyToPhoneNumbers.get(company.id);
            if (arr == null) {
              arr = [];
              companyToPhoneNumbers.set(company.id, arr);
            }
            arr.push(n);
          }
        }
      });
    }
    const groups: ProductPhoneNumberGroup[] = [];
    companyToPhoneNumbers.forEach((arr, companyId) => {
      if (CollectionHelper.isPopulated(arr)) {
        const company = this.projectContactToCompany.get(
          arr[0].projectContact.id
        );
        if (company != null) {
          const group: ProductPhoneNumberGroup = {
            companyId: companyId,
            companyName: company.regularName,
            phoneNumbers: arr.map((n) =>
              this.formatPhoneNumber.transform(n.phoneNumbers)
            ),
          };
          groups.push(group);
        }
      }
    });
    this.productPhoneNumberGroups = groups;
    this.multiplePhoneNumbers =
      groups.length > 1 || groups.some((g) => g.phoneNumbers.length > 1);
  }

  calculateProductPhoneNumbersClient() {
    const groups: ProductPhoneNumberGroup[] = [];

    if (
      CollectionHelper.isPopulated(this.inputProject.projectContacts) &&
      CollectionHelper.isPopulated(this.inputBoard.productPhoneNumbers)
    ) {
      const group: ProductPhoneNumberGroup = {
        companyId: this.inputProject.projectContacts[0].company.id,
        companyName: this.inputProject.projectContacts[0].company.regularName,
        phoneNumbers: this.inputBoard.productPhoneNumbers.map((n) =>
          this.formatPhoneNumber.transform(n.phoneNumbers)
        ),
      };
      groups.push(group);
    }

    this.productPhoneNumberGroups = groups;
    this.multiplePhoneNumbers =
      groups.length > 1 || groups.some((g) => g.phoneNumbers.length > 1);
  }

  calculatePricePreviewDisplay(): void {
    if (!CollectionHelper.isPopulated(this.inputBoard.boardParts)) {
      this.atLeastOneSignSize = false;
      return;
    }

    let atLeastOneSignSize = false;
    let atLeastOneNoSignSize = false;
    let total = 0;

    this.inputBoard.boardParts.forEach((bp) => {
      bp.showDistanceToBottom =
        BoardPartFormHelper.calculateShowDistanceToBottomSelector(bp);
      atLeastOneSignSize = atLeastOneSignSize || bp.signSize != null;
      atLeastOneNoSignSize = atLeastOneNoSignSize || bp.signSize == null;
      if (bp.signSize != null) {
        total += ProductHelper.getBoardPartSignSizeSubtotal(
          bp,
          this.atLeastOneRetailer
        );
      }
    });

    this.atLeastOneSignSize = atLeastOneSignSize;
    this.atLeastOneNoSignSize = atLeastOneNoSignSize;
    this.totalPrice = total;
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
